<script setup lang="ts">
  import { ref, computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { ArrowLeft, Loader2 } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import {
    OrganisationsService,
    UsersService,
    type CustomerUser,
    RoleEnum,
    FlagEnum
  } from '@/types/generated'
  import { ROUTES } from '@/router/routes'
  import { useTranslation } from '@/composables/useTranslation'
  import { useToast } from '@/components/ui/toast'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { onMounted } from 'vue'

  const { t } = useTranslation({
    en: {
      backToTeam: 'Back to team',
      createEmployee: 'Create Employee',
      employeeInfo: 'Employee Information',
      configureSettings: 'Enter the details for the new team member',
      email: 'Email',
      enterEmail: 'Enter email address',
      firstName: 'First Name',
      enterFirstName: 'Enter first name',
      lastName: 'Last Name',
      enterLastName: 'Enter last name',
      role: 'Role',
      selectRole: 'Select a role',
      creating: 'Creating...',
      create: 'Create Employee',
      errorCreating: 'Error creating employee',
      successCreating: 'Employee created successfully',
      lawyer: 'Lawyer',
      admin: 'Admin',
      clerk: 'Clerk',
      phoneAiUser: 'Phone AI user',
      emailRequired: 'Email is required',
      firstNameRequired: 'First name is required',
      lastNameRequired: 'Last name is required',
      invalidEmail: 'Please enter a valid email address',
      welcomeEmailSent: 'A welcome email with login credentials has been sent to the new employee.'
    },
    de: {
      backToTeam: 'Zurück zum Team',
      createEmployee: 'Mitarbeiter erstellen',
      employeeInfo: 'Mitarbeiterinformationen',
      configureSettings: 'Geben Sie die Details für das neue Teammitglied ein',
      email: 'E-Mail',
      enterEmail: 'E-Mail-Adresse eingeben',
      firstName: 'Vorname',
      enterFirstName: 'Vorname eingeben',
      lastName: 'Nachname',
      enterLastName: 'Nachname eingeben',
      role: 'Rolle',
      selectRole: 'Rolle auswählen',
      creating: 'Wird erstellt...',
      create: 'Mitarbeiter erstellen',
      errorCreating: 'Fehler beim Erstellen des Mitarbeiters',
      successCreating: 'Mitarbeiter erfolgreich erstellt',
      lawyer: 'Anwalt',
      admin: 'Administrator',
      clerk: 'Sachbearbeiter',
      phoneAiUser: 'Telefon-KI-Nutzer',
      emailRequired: 'E-Mail ist erforderlich',
      firstNameRequired: 'Vorname ist erforderlich',
      lastNameRequired: 'Nachname ist erforderlich',
      invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
      welcomeEmailSent:
        'Eine Willkommens-E-Mail mit den Anmeldedaten wurde an den neuen Mitarbeiter gesendet.'
    }
  })

  const router = useRouter()
  const { toast } = useToast()

  const currentUser = ref<CustomerUser | null>(null)
  const isLoading = ref(false)
  const isInitialLoading = ref(true)

  const form = ref({
    email: '',
    first_name: '',
    last_name: '',
    role: RoleEnum.LAWYER
  })

  const errors = ref<Record<string, string>>({})

  const phonebotOnlyRoleFlag = useFeatureFlag(FlagEnum.PHONEBOT_ONLY_ROLE)

  const roleOptions = computed(() => {
    const options = [
      { value: RoleEnum.LAWYER, title: t('lawyer') },
      { value: RoleEnum.ADMIN, title: t('admin') },
      { value: RoleEnum.CLERK, title: t('clerk') }
    ]
    if (phonebotOnlyRoleFlag.isEnabled.value) {
      options.push({ value: RoleEnum.PHONE_AI_USER, title: t('phoneAiUser') })
    }
    return options
  })

  onMounted(async () => {
    phonebotOnlyRoleFlag.checkFlag()
    try {
      currentUser.value = await UsersService.usersRetrieve({ uuid: 'current' })
    } catch (error) {
      console.error('Failed to fetch user:', error)
      router.push(ROUTES.APP.SETTINGS.LAWFIRM.TEAM)
    } finally {
      isInitialLoading.value = false
    }
  })

  const validateForm = (): boolean => {
    errors.value = {}

    if (!form.value.email.trim()) {
      errors.value.email = t('emailRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
      errors.value.email = t('invalidEmail')
    }

    if (!form.value.first_name.trim()) {
      errors.value.first_name = t('firstNameRequired')
    }

    if (!form.value.last_name.trim()) {
      errors.value.last_name = t('lastNameRequired')
    }

    return Object.keys(errors.value).length === 0
  }

  const goBack = () => {
    router.push(ROUTES.APP.SETTINGS.LAWFIRM.TEAM)
  }

  const createEmployee = async () => {
    if (!validateForm()) {
      return
    }

    try {
      isLoading.value = true

      await OrganisationsService.organisationsEmployeesCreateCreate({
        uuid: currentUser.value?.org?.uuid || '',
        formData: {
          email: form.value.email.trim().toLowerCase(),
          first_name: form.value.first_name.trim(),
          last_name: form.value.last_name.trim(),
          role: form.value.role
        }
      })

      toast({
        title: t('successCreating'),
        description: t('welcomeEmailSent')
      })

      router.push(ROUTES.APP.SETTINGS.LAWFIRM.TEAM)
    } catch (error: any) {
      console.error('Failed to create employee:', error)

      let errorMessage = ''
      if (error?.body?.email) {
        errorMessage = error.body.email[0]
      } else if (error?.body?.error) {
        errorMessage = error.body.error
      } else if (error?.message) {
        errorMessage = error.message
      } else {
        errorMessage = 'An error occurred'
      }

      toast({
        title: t('errorCreating'),
        description: errorMessage,
        variant: 'destructive'
      })
    } finally {
      isLoading.value = false
    }
  }

  const isFormValid = computed(() => {
    return (
      form.value.email.trim() &&
      form.value.first_name.trim() &&
      form.value.last_name.trim() &&
      form.value.role
    )
  })
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Loading state -->
    <div
      v-if="isInitialLoading"
      class="flex min-h-screen items-center justify-center"
    >
      <Loader2 class="h-8 w-8 animate-spin text-purple-600" />
    </div>

    <template v-else>
      <!-- Header -->
      <div class="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6">
        <div class="flex items-center gap-4">
          <button
            class="flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-gray-900"
            @click="goBack"
          >
            <ArrowLeft class="h-4 w-4" />
            {{ t('backToTeam') }}
          </button>
        </div>
      </div>

      <!-- Main content -->
      <div class="p-6">
        <div class="mx-auto max-w-2xl">
          <div class="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
            <div class="space-y-8">
              <!-- Form Header -->
              <div>
                <h2 class="mb-2 text-lg font-medium text-gray-900">{{ t('employeeInfo') }}</h2>
                <p class="text-sm text-gray-600">{{ t('configureSettings') }}</p>
              </div>

              <!-- Email -->
              <div>
                <InputField
                  v-model="form.email"
                  name="email"
                  type="email"
                  :label="t('email')"
                  :placeholder="t('enterEmail')"
                  required
                />
                <div
                  v-if="errors.email"
                  class="mt-1 text-sm text-red-600"
                >
                  {{ errors.email }}
                </div>
              </div>

              <!-- First Name -->
              <div>
                <InputField
                  v-model="form.first_name"
                  name="first_name"
                  :label="t('firstName')"
                  :placeholder="t('enterFirstName')"
                  required
                />
                <div
                  v-if="errors.first_name"
                  class="mt-1 text-sm text-red-600"
                >
                  {{ errors.first_name }}
                </div>
              </div>

              <!-- Last Name -->
              <div>
                <InputField
                  v-model="form.last_name"
                  name="last_name"
                  :label="t('lastName')"
                  :placeholder="t('enterLastName')"
                  required
                />
                <div
                  v-if="errors.last_name"
                  class="mt-1 text-sm text-red-600"
                >
                  {{ errors.last_name }}
                </div>
              </div>

              <!-- Role -->
              <div>
                <SelectField
                  v-model="form.role"
                  :label="t('role')"
                  name="role"
                  :options="roleOptions"
                  :placeholder="t('selectRole')"
                  required
                />
              </div>

              <!-- Create Button -->
              <div class="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  @click="goBack"
                >
                  {{ t('backToTeam') }}
                </Button>
                <Button
                  :disabled="isLoading || !isFormValid"
                  class="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
                  @click="createEmployee"
                >
                  <Loader2
                    v-if="isLoading"
                    class="h-4 w-4 animate-spin"
                  />
                  {{ isLoading ? t('creating') : t('create') }}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
